<template>
  <div class="p-lg pb-leftSquiggleHeight">
    <h1>Dropdowns</h1>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Option component</h2>
      <h3>Enabled</h3>
      <BaseOption
        :action="() => {}"
        text="Option"
      />
      <h3 class="mt-base">Disabled</h3>
      <BaseOption
        :action="() => {}"
        text="Option"
        :disabled="true"
      />
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Dropdown component</h2>
      <h3>Enabled</h3>
      <BaseDropdown
        text="Dropdown"
        v-model="basicOptions"
        :dropdownIsOnRightHandSide="false"
      />
      <h3 class="mt-base">Disabled</h3>
      <BaseDropdown
        text="Dropdown"
        v-model="basicOptions"
        :dropdownIsOnRightHandSide="false"
        :disabled="true"
      />
      <h3 class="mt-base">On the right hand side of its container - options set to jut out to the left</h3>
      <div class="flex justify-end border-solid border-placeholderGrey border p-base rounded">
        <BaseDropdown
          text="Dropdown"
          v-model="basicOptions"
        />
      </div>
      <h3 class="mt-base">On the left hand side of its container - options set to jut out to the right</h3>
      <div class="border-solid border-placeholderGrey border p-base rounded">
        <BaseDropdown
          text="Dropdown"
          v-model="basicOptions"
          :dropdownIsOnRightHandSide="false"
        />
      </div>
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white pb-xl">
      <h2>Adding things to a list Demo</h2>
      <h3> - Adding disables option, to prevent same selection being added twice</h3>
      <h3> - When all options have been selected and are now disabled, the dropdown itself becomes disabled</h3>
      <h3> Just an example of the kind of things we could do with this component</h3>

      <div class="flex gap-base items-center">
        <BaseDropdown
          text="Animals"
          v-model="demoOptionsRef"
          :dropdownIsOnRightHandSide="false"
          :disabled="demoDisabled"
        />
        <div class="flex flex-wrap gap-xs">
          <DismissibleInfoPill
            v-for="item in animalsList"
            @dismiss="removeFromAnimalsList"
            :key="item"
            :text="item"
            :id="item"
          >
            {{ item }}
          </DismissibleInfoPill>
        </div>
      </div>
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white pb-xl">
      <h2>Dropdowns will open downwards by default. They will open upwards however, if they are situated too close to the bottom of the page.</h2>
      <BaseDropdown
        text="Oh no! I'm too close to the bottom"
        v-model="nearBottomOptions"
        :dropdownIsOnRightHandSide="false"
      />
    </div>
  </div>
</template>

<script setup>
// @ts-check

import { ref } from 'vue';

import { BaseDropdown } from '@/components/library/dropdowns/BaseDropdown';
import { BaseOption } from '@/components/library/dropdowns/BaseOption';
import { DismissibleInfoPill } from '@/components/library/pills/DismissibleInfoPill';

const basicOptions = ref([
  {
    action: () => {},
    text: 'Option 1',
    disabled: false,
  },
  {
    action: () => {},
    text: 'Option 2',
    disabled: false,
  },
  {
    action: () => {},
    text: 'Option 3',
    disabled: false,
  },
  {
    action: () => {},
    text: 'Option 4 - disabled',
    disabled: true,
  },
]);
const animalsList = ref([]);

const demoOptions = () => ([
  {
    // eslint-disable-next-line no-use-before-define
    action: () => addAnimalToList('Lion'),
    text: 'Lion',
    disabled: false,
  },
  {
    // eslint-disable-next-line no-use-before-define
    action: () => addAnimalToList('Aardvark'),
    text: 'Aardvark',
    disabled: false,
  },
  {
    // eslint-disable-next-line no-use-before-define
    action: () => addAnimalToList('Marmoset'),
    text: 'Marmoset',
    disabled: false,
  },
  {
    // eslint-disable-next-line no-use-before-define
    action: () => addAnimalToList('Salamander'),
    text: 'Salamander',
    disabled: false,
  },
  {
    // eslint-disable-next-line no-use-before-define
    action: () => addAnimalToList('Mouse'),
    text: 'Mouse',
    disabled: false,
  },
  {
    // eslint-disable-next-line no-use-before-define
    action: () => addAnimalToList('Shark'),
    text: 'Shark',
    disabled: false,
  },
]);

const demoOptionsRef = ref(demoOptions());

const demoDisabled = ref(false);

function addAnimalToList(animal) {
  animalsList.value.push(animal);
  // disable option after selection
  const optionToDisable = demoOptionsRef.value.find(opt => opt.text === animal);
  optionToDisable.disabled = true;
  // if all options are selected, disable dropdown itself
  if (demoOptionsRef.value.every(opt => opt.disabled)) {
    demoDisabled.value = true;
  } else {
    demoDisabled.value = false;
  }
}

async function removeFromAnimalsList(id) {
  // re-enable option
  const option = demoOptionsRef.value.find(opt => opt.text === id);
  option.disabled = false;
  demoDisabled.value = false;
  // remove from list
  const index = animalsList.value.findIndex(opt => opt === id);
  animalsList.value.splice(index, 1);
}

const nearBottomOptions = ref([
  {
    action: () => {},
    text: "Don't worry",
    disabled: false,
  },
  {
    action: () => {},
    text: 'We open',
    disabled: false,
  },
  {
    action: () => {},
    text: 'Upwards',
    disabled: false,
  },
]);
</script>

